import {
  Checklist,
  DossierTable,
  Field,
  FieldGroup,
  FieldProse,
  Gating,
  NumberedSection,
  Placeholder,
  Section16SoftwareDoc,
  Section17CyberInteropDoc,
  Section6SamdFields,
  canonNA,
  hasReal,
  obj,
  prose,
  withInlinePlaceholders,
} from './DossierPrimitives';
import { IvdDeviceDescription, IvdPerformance, IvdShelfLife } from './IvdSections';
import { StructuredCoverLetter } from './CoverLetterSection';

/**
 * eSTAR De Novo Classification Request renderer — 21 sections per the FDA
 * eSTAR De Novo Table 1 (guidance FDA-2023-D-3788), mirroring the canonical
 * spine `app/dossier/usa/denovo_spine.py` and the prompt
 * `app/dossier/usa/prompts_estar_denovo.py`. Schema lives in
 * `backend/schemas_denovo.py` (DeNovoDossier).
 *
 * Schema detection (in the parent renderer): submission_type starts with
 * "denovo" — De Novo and 510(k) eSTAR share the `section_NN_` key naming but
 * the SECTION CONTENT differs structurally:
 *   - §8 Classification merges the former Proposed Classification + De Novo
 *     Justification (860.220(a)(11)+(a)(8)); §9 Benefits, Risks & Mitigation
 *     Measures merges the former Risk-Benefit + Identified Risks + Special
 *     Controls (860.220(a)(9)+(a)(10)+(a)(14)). Neither has a 510(k) analog.
 *   - §6 Device Description adds the 860.220(a)(6)/(a)(7) pictorials /
 *     components / conditions-of-use / accessories / alternative-practices.
 *   - The former §13 Design Controls is removed (a QSR concept named nowhere
 *     in 860.220).
 *
 * FE↔BE lockstep with `_render_estar_denovo_sections_new_schema` in
 * `backend/services/dossier/estar_renderer/_denovo.py`.
 */
export function DeNovoRenderer({
  dossier,
  ivd = false,
}: {
  dossier: Record<string, unknown>;
  /** IVD De Novo: swap §6/§13/§18 for their IVD variants. */
  ivd?: boolean;
}) {
  return (
    <div className="space-y-4">
      <Section1 dossier={dossier} />
      <Section2 dossier={dossier} />
      <Section3 dossier={dossier} />
      <Section4 dossier={dossier} />
      <Section5 dossier={dossier} />
      {ivd ? (
        <IvdDeviceDescription dossier={dossier} sectionKey="section_06_device_description" num={6} />
      ) : (
        <Section6 dossier={dossier} />
      )}
      <Section7 dossier={dossier} />
      <Section8 dossier={dossier} />
      <Section9 dossier={dossier} />
      <Section10 dossier={dossier} />
      <Section11 dossier={dossier} />
      <Section12 dossier={dossier} />
      {ivd ? (
        <IvdShelfLife dossier={dossier} sectionKey="section_13_shelf_life" num={13} />
      ) : (
        <Section13 dossier={dossier} />
      )}
      <Section14 dossier={dossier} />
      <Section15 dossier={dossier} />
      <Section16 dossier={dossier} />
      <Section17 dossier={dossier} />
      {ivd ? (
        <IvdPerformance dossier={dossier} sectionKey="section_18_performance_testing" num={18} />
      ) : (
        <Section18 dossier={dossier} />
      )}
      <Section19 dossier={dossier} />
      <Section20 dossier={dossier} />
      <Section21 dossier={dossier} />
    </div>
  );
}

// ── §1 Submission Type ──────────────────────────────────────────────────

function Section1({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_01_submission_type');
  return (
    <NumberedSection prefix="Section" num={1} title="Submission Type">
      <Field
        label="De Novo variant:"
        value={s.submission_type}
        fallbackHint="De Novo Classification Request — §513(f)(2) variant label"
      />
    </NumberedSection>
  );
}

// ── §2 Cover Letter ─────────────────────────────────────────────────────

function Section2({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_02_cover_letter');
  return (
    <NumberedSection prefix="Section" num={2} title="Cover Letter">
      <StructuredCoverLetter
        s2={s}
        submissionTypeRowLabel="Type of De Novo Submission"
        reasonHeading="Reason for the De Novo Submission"
        reasonPlaceholder="Reason for the De Novo submission"
        estarStatement="This electronic signed cover letter is enclosed with an eSTAR PDF as an embedded attachment. We are submitting this eSTAR PDF via the FDA Customer Collaboration Portal (CCP)."
      />
    </NumberedSection>
  );
}

// ── §3 Applicant Information (same shape as 510(k) §3) ──────────────────

function Section3({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_03_applicant_information');
  return (
    <NumberedSection prefix="Section" num={3} title="Applicant Information">
      <Field label="Applicant (firm) name:" value={s.firm_name} />
      <Field
        label="Establishment registration number:"
        value={s.establishment_registration_number}
      />
      <Field label="Owner/Operator number:" value={s.owner_operator_number} />
      <Field label="Street address:" value={s.street_address} />
      <Field
        label="U.S. Agent (required for foreign applicants):"
        value={s.us_agent}
      />
      <Field
        label="Official correspondent / contact person:"
        value={s.official_correspondent}
      />
    </NumberedSection>
  );
}

// ── §4 Pre-Submission Correspondence (same shape as 510(k) §4) ─────────

function Section4({ dossier }: { dossier: Record<string, unknown> }) {
  return (
    <NumberedSection
      prefix="Section"
      num={4}
      title="Pre-Submission Correspondence & Previous Regulator Interaction"
    >
      <Gating
        question="Has the applicant submitted a Pre-Submission (Q-Sub), Submission Issue Request, or any prior interaction with FDA for this device?"
        section={obj(dossier, 'section_04_pre_submission_correspondence')}
      />
    </NumberedSection>
  );
}

// ── §5 Consensus Standards — 860.220(a)(12) (same shape as 510(k) §5) ──

function Section5({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_05_consensus_standards');
  const rows = Array.isArray(s.standards)
    ? (s.standards as Record<string, unknown>[]).map((r) => [
        r.fda_recognition_number,
        r.standard_designation,
        r.title,
        r.conformance,
      ])
    : [];
  return (
    <NumberedSection prefix="Section" num={5} title="Consensus Standards">
      <FieldGroup label="Standards entered via eSTAR dropdown (FDA-recognized):">
        <DossierTable
          headers={['FDA Rec. #', 'Standard', 'Title', 'Conformance']}
          rows={rows}
        />
      </FieldGroup>
      <FieldProse
        label="Declaration of Conformity:"
        body={prose(s.declaration_of_conformity)}
      />
    </NumberedSection>
  );
}

// ── §6 Device Description — 860.220(a)(6)+(a)(7) (De Novo-forked: SaMD
//    superset + pictorials/components/conditions-of-use/accessories/alt-practices) ──

function Section6({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_06_device_description');
  return (
    <NumberedSection prefix="Section" num={6} title="Device Description">
      <Field label="Device trade name / proprietary name:" value={s.trade_name} />
      <Field label="Common or usual name:" value={s.common_name} />
      <Field label="Models / sizes included in this submission:" value={s.models_included} />
      <Field label="FDA device listing number (if listed):" value={canonNA(s.fda_listing_number)} />
      <Field label="Request for Designation (RFD) number (if any):" value={canonNA(s.rfd_number)} />
      <Section6SamdFields s={s} />
      {/* 860.220(a)(6)/(a)(7) are REQUIRED elements — always render (placeholder chip
          when empty) so a required element is never silently dropped. Only the
          SaMD-vs-hardware-adaptive detailed_description is omit-when-empty. */}
      <FieldProse
        label="Conditions of use (a)(6)(iv):"
        body={prose(s.conditions_of_use)}
        fallbackHint="Setting of use, intended user, anatomical location / user interface, and any device-with-device interactions"
      />
      <FieldProse
        label="Pictorial representations (a)(6)(i):"
        body={prose(s.pictorial_representations)}
        fallbackHint="Device figures / photographs / engineering drawings (submitter attachment)"
      />
      <FieldProse
        label="Functional components (a)(6)(ii):"
        body={prose(s.functional_components)}
        fallbackHint="Significant physical and/or software components of the device"
      />
      <FieldProse
        label="Accessories (a)(6)(v):"
        body={prose(s.accessories)}
        fallbackHint="Marketed accessories intended for use with the device + FDA reference numbers, or a statement that none is provided"
      />
      <FieldProse
        label="Alternative practices / procedures (a)(7):"
        body={prose(s.alternative_practices)}
        fallbackHint="Existing alternative practices or procedures for the disease/condition, or a statement that none is known"
      />
      {hasReal(prose(s.detailed_description)) && (
        <FieldProse label="Detailed device description:" body={prose(s.detailed_description)} />
      )}
    </NumberedSection>
  );
}

// ── §7 Proposed Indications for Use — 860.220(a)(5) (same shape as 510(k) §7) ──

function Section7({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_07_indications_for_use');
  return (
    <NumberedSection prefix="Section" num={7} title="Proposed Indications for Use">
      <FieldProse
        label="Indications for Use statement:"
        body={prose(s.indications_statement)}
      />
      <Field label="Prescription use (Rx) — 21 CFR 801.109:" value={s.prescription_use} />
      <Field label="Over-the-counter use:" value={s.otc_use} />
      <Field label="Life-supporting or life-sustaining device?" value={s.life_supporting} />
      <Field label="Intended for pediatric use?" value={s.pediatric_use} />
    </NumberedSection>
  );
}

// ── §8 Classification — 860.220(a)(11)+(a)(8) (merges the former Proposed
//    Classification + De Novo Justification) ─────────────────────────────

function Section8({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_08_classification');
  return (
    <NumberedSection prefix="Section" num={8} title="Classification">
      <Field label="Proposed device class (I or II):" value={s.proposed_device_class} />
      <Field
        label="Proposed regulation number (21 CFR):"
        value={s.proposed_regulation_number}
      />
      <Field label="Proposed FDA product code:" value={s.proposed_product_code} />
      <Field label="Review panel:" value={s.review_panel} />
      <FieldProse
        label="Basis for absence of a legally marketed device of comparable type (a)(8)(i)(A):"
        body={prose(s.no_predicate_basis)}
        fallbackHint="FDA 510(k)/De Novo database search outcome demonstrating no legally marketed device of comparable type"
      />
      <FieldProse
        label="Similar/related device types and differentiation (a)(8)(i)(B)&(C):"
        body={prose(s.classification_summary)}
        fallbackHint="Any potentially-similar device types identified and how the subject device differs (intended use, technology, patient population)"
      />
      <Field
        label="Automatic Class III designation absent this request:"
        value={s.automatic_class_iii_designation}
        fallbackHint="Yes / No — whether device defaults to Class III under §513(f)(1)"
      />
      <FieldProse
        label="Classification rationale — why general/special controls provide reasonable assurance (a)(11):"
        body={prose(s.rationale)}
        fallbackHint="Why De Novo is appropriate + why general (Class I) or general + special (Class II) controls provide reasonable assurance of safety and effectiveness"
      />
    </NumberedSection>
  );
}

// ── §9 Benefits, Risks & Mitigation Measures — 860.220(a)(9)+(a)(10)+(a)(14)
//    (merges the former Risk-Benefit + Identified Risks + Special Controls) ──

function Section9({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_09_benefits_risks_mitigations');
  const rows = Array.isArray(s.risks_table)
    ? (s.risks_table as Record<string, unknown>[]).map((r) => [
        r.identified_risk,
        r.mitigation_measure,
        r.verification,
      ])
    : [];
  const specialIsYes = String(s.special_controls_applicable ?? 'No') === 'Yes';
  return (
    <NumberedSection
      prefix="Section"
      num={9}
      title="Benefits, Risks, and Mitigation Measures"
    >
      <FieldProse
        label="Probable benefits (a)(14):"
        body={prose(s.probable_benefits)}
        fallbackHint="Nature, magnitude, probability, duration of benefit per FDA Factors guidance (2019)"
      />
      <FieldProse
        label="Probable risks (a)(14):"
        body={prose(s.probable_risks)}
        fallbackHint="Nature, severity, probability, reversibility, duration of risk"
      />
      <FieldProse
        label="Benefit-risk determination — valid scientific evidence per 21 CFR 860.7(c):"
        body={prose(s.benefit_risk_determination)}
        fallbackHint="Probable benefit outweighs probable risk for the indicated population under general (or general + special) controls"
      />
      <FieldGroup label="Identified risks → mitigations → verification (a)(9):">
        <DossierTable
          headers={['Identified Risk', 'Mitigation Measure', 'Verification']}
          rows={rows}
        />
      </FieldGroup>
      <Field
        label="Special controls applicable (Class II — 21 CFR 860.220(a)(10)):"
        value={s.special_controls_applicable}
        fallbackHint="Yes (Class II) / No (Class I)"
      />
      {specialIsYes && (
        <>
          <FieldProse
            label="Proposed performance standards:"
            body={prose(s.performance_standards)}
            fallbackHint="Submitter must specify proposed performance standards per 21 CFR 860.220(a)(10)"
          />
          <FieldProse
            label="Proposed labeling requirements:"
            body={prose(s.labeling_requirements)}
            fallbackHint="Submitter must specify proposed labeling requirements"
          />
          <FieldProse
            label="Proposed training requirements:"
            body={prose(s.training_requirements)}
            fallbackHint="Submitter must specify proposed training requirements (if applicable)"
          />
          <FieldProse
            label="Proposed post-market surveillance:"
            body={prose(s.post_market_surveillance)}
            fallbackHint="Submitter must specify proposed post-market surveillance / registry / reporting requirements (if applicable)"
          />
        </>
      )}
      <FieldProse
        label="Discussion of risks-and-mitigations table:"
        body={prose(s.discussion)}
        fallbackHint="Synthesis bridging the risks table + special controls to the benefit-risk determination"
      />
    </NumberedSection>
  );
}

// ── §10 Labeling — 860.220(a)(18) (same shape as 510(k) §11) ───────────

function Section10({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_10_labeling');
  return (
    <NumberedSection prefix="Section" num={10} title="Labeling">
      <FieldGroup label="Labeling components included:">
        <Checklist items={s.labeling_components as unknown[] | undefined} />
      </FieldGroup>
      <FieldGroup label="Required content checklist (21 CFR 801):">
        <Checklist items={s.required_content_checklist as unknown[] | undefined} />
      </FieldGroup>
      <FieldProse
        label="Device label text:"
        body={prose(s.device_label_text)}
        fallbackHint="Verbatim device label artwork — submitter-provided regulatory artifact"
      />
      <FieldProse
        label="Instructions for Use (IFU) text:"
        body={prose(s.ifu_text)}
        fallbackHint="Verbatim IFU content — submitter-provided regulatory artifact"
      />
    </NumberedSection>
  );
}

// ── §11 Reprocessing (same shape as 510(k) §12) ────────────────────────

function Section11({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_11_reprocessing');
  return (
    <NumberedSection prefix="Section" num={11} title="Reprocessing">
      <Field label="Reusable device?" value={canonNA(s.is_reusable)} />
      <Field
        label="Reprocessing method (cleaning + sterilization/disinfection):"
        value={canonNA(s.reprocessing_method)}
      />
      <Field label="Reprocessing procedure validated?" value={s.reprocessing_validated} />
      <FieldProse label="Validation summary:" body={prose(s.validation_summary)} />
    </NumberedSection>
  );
}

// ── §12 Sterility (same shape as 510(k) §13) ───────────────────────────

function Section12({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_12_sterility');
  return (
    <NumberedSection prefix="Section" num={12} title="Sterility">
      <Field label="Provided sterile?" value={canonNA(s.provided_sterile)} />
      <Field label="Sterilization method:" value={canonNA(s.sterilization_method)} />
      <FieldProse
        label="Sterility validation summary:"
        body={prose(s.sterility_validation)}
      />
    </NumberedSection>
  );
}

// ── §13 Shelf Life (same shape as 510(k) §14) ──────────────────────────

function Section13({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_13_shelf_life');
  return (
    <NumberedSection prefix="Section" num={13} title="Shelf Life">
      <Field
        label="Device claims a defined shelf life / service life?"
        value={canonNA(s.has_shelf_life)}
      />
      <Field label="Shelf life / service life duration:" value={canonNA(s.shelf_life_duration)} />
      <FieldProse
        label="Shelf life / service life validation:"
        body={prose(s.shelf_life_validation)}
      />
    </NumberedSection>
  );
}

// ── §14 Biocompatibility (same shape as 510(k) §15) ────────────────────

function Section14({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_14_biocompatibility');
  const rows = Array.isArray(s.endpoints_table)
    ? (s.endpoints_table as Record<string, unknown>[]).map((r) => [
        r.endpoint,
        r.method_standard,
        r.sample,
        r.result,
        r.date,
      ])
    : [];
  return (
    <NumberedSection prefix="Section" num={14} title="Biocompatibility">
      <Field label="Patient contact category:" value={canonNA(s.patient_contact)} />
      <Field label="ISO 10993-1 categorization:" value={canonNA(s.iso_categorization)} />
      <FieldGroup label="Endpoints evaluated:">
        <DossierTable
          headers={['Endpoint', 'Method (standard)', 'Sample', 'Result', 'Date']}
          rows={rows}
        />
      </FieldGroup>
      <Field label="Testing laboratory:" value={canonNA(s.testing_laboratory)} />
      <FieldProse label="Detailed results:" body={prose(s.detailed_results)} />
      <FieldProse label="Conclusion:" body={prose(s.conclusion)} />
    </NumberedSection>
  );
}

// ── §15 Software/Firmware — 860.220(a)(15)(ii) (gated; same as 510(k) §16) ──

function Section15({ dossier }: { dossier: Record<string, unknown> }) {
  return <Section16SoftwareDoc dossier={dossier} sectionKey="section_15_software_firmware" num={15} />;
}

// ── §16 Cybersecurity / Interoperability (FDA cyber Appendix 4 + interop §VI;
//    shared with 510(k) §17 / PMA §17; gating block when no cyber) ──────────

function Section16({ dossier }: { dossier: Record<string, unknown> }) {
  return <Section17CyberInteropDoc dossier={dossier} sectionKey="section_16_cybersecurity" num={16} />;
}

// ── §17 EMC / Electrical Safety (gated, same as 510(k) §18) ────────────

function Section17({ dossier }: { dossier: Record<string, unknown> }) {
  return (
    <NumberedSection
      prefix="Section"
      num={17}
      title="EMC, Electrical, Mechanical, Wireless and Thermal Safety"
    >
      <Gating
        question="Does the device have electrical, electronic, electromechanical, or wireless components?"
        section={obj(dossier, 'section_17_electrical_safety_emc')}
      />
    </NumberedSection>
  );
}

// ── §18 Performance Testing (same shape as 510(k) §19) ─────────────────

function Section18({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_18_performance_testing');
  const bench = obj(s, 'bench_testing');
  const animal = obj(s, 'animal_testing');
  const clinical = obj(s, 'clinical_testing');

  const benchRows = Array.isArray(bench.performance_test_table)
    ? (bench.performance_test_table as Record<string, unknown>[]).map((r) => [
        r.number,
        r.test,
        r.acceptance_criterion,
        r.n,
        r.result,
      ])
    : [];

  const clinicalIsYes = String(clinical.gating_response ?? 'No') === 'Yes';

  return (
    <NumberedSection prefix="Section" num={18} title="Performance Testing">
      <h4 className="text-[14px] font-bold text-navy mt-2 mb-2">
        Non-Clinical Bench Performance Testing
      </h4>
      <Field label="Bench testing used?" value={bench.bench_used} />
      <Field label="Testing facility:" value={bench.testing_facility} />
      <FieldGroup label="Performance test plan + outcomes:">
        <DossierTable
          headers={['#', 'Test', 'Acceptance criterion', 'n', 'Result']}
          rows={benchRows}
        />
      </FieldGroup>
      <FieldProse label="Detailed results:" body={prose(bench.detailed_results)} />
      <FieldProse label="Conclusion:" body={prose(bench.conclusion)} />

      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">
        Animal Performance Studies
      </h4>
      <Gating
        question="Were animal performance studies conducted to support this submission?"
        section={animal}
      />

      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">
        Clinical Performance Studies
      </h4>
      {clinicalIsYes ? (
        <>
          <Field label="Are clinical performance studies relied upon?" value="Yes." />
          {prose(clinical.study_summary) != null && (
            <FieldProse label="Study summary:" body={prose(clinical.study_summary)} />
          )}
          {prose(clinical.efficacy_results) != null && (
            <FieldProse label="Efficacy results:" body={prose(clinical.efficacy_results)} />
          )}
          {prose(clinical.safety_analysis) != null && (
            <FieldProse label="Safety analysis:" body={prose(clinical.safety_analysis)} />
          )}
          {prose(clinical.risk_benefit_analysis) != null && (
            <FieldProse
              label="Risk-benefit analysis:"
              body={prose(clinical.risk_benefit_analysis)}
            />
          )}
          {prose(clinical.statistical_analysis) != null && (
            <FieldProse
              label="Statistical analysis:"
              body={prose(clinical.statistical_analysis)}
            />
          )}
        </>
      ) : (
        <Gating
          question="Are clinical performance studies relied upon to support this De Novo request?"
          section={clinical}
        />
      )}
    </NumberedSection>
  );
}

// ── §19 References — 860.220(a)(16) ────────────────────────────────────

function Section19({ dossier }: { dossier: Record<string, unknown> }) {
  const refs = Array.isArray(dossier.section_19_references)
    ? (dossier.section_19_references as unknown[])
    : [];
  return (
    <NumberedSection prefix="Section" num={19} title="References">
      <FieldGroup
        label="References:"
        fallbackHint="Literature references and FDA records cited in this submission"
      >
        {refs.length > 0 ? (
          <ol className="list-decimal pl-5 m-0 space-y-1 text-[13.5px]">
            {refs.map((s, i) => (
              <ReferenceItem key={i} src={s} />
            ))}
          </ol>
        ) : null}
      </FieldGroup>
    </NumberedSection>
  );
}

function ReferenceItem({ src }: { src: unknown }) {
  if (!src || typeof src !== 'object') return <li>{String(src)}</li>;
  const s = src as {
    identifier?: string;
    title?: string;
    url?: string;
    tool?: string;
    note?: string;
  };
  return (
    <li>
      {s.identifier && (
        <span className="font-mono text-[12.5px] text-electric mr-1">
          {s.identifier}
        </span>
      )}
      {s.url && s.title ? (
        <a
          href={s.url}
          target="_blank"
          rel="noopener"
          className="text-navy underline decoration-line-strong hover:decoration-electric"
        >
          {s.title}
        </a>
      ) : s.url ? (
        <a
          href={s.url}
          target="_blank"
          rel="noopener"
          className="text-navy underline decoration-line-strong hover:decoration-electric"
        >
          {s.url}
        </a>
      ) : s.title ? (
        <span>{s.title}</span>
      ) : null}
      {s.tool && (
        <span className="ml-2 text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted bg-[#f3f4f6] border border-line rounded-full py-0.5 px-2">
          {s.tool}
        </span>
      )}
      {s.note && <span className="text-muted"> — {s.note}</span>}
    </li>
  );
}

// ── §20 Administrative Documentation (De Novo summary instead of 510(k) summary) ──

function Section20({ dossier }: { dossier: Record<string, unknown> }) {
  const s = obj(dossier, 'section_20_administrative_documentation');
  const summary = obj(s, 'de_novo_summary');
  const tas = obj(s, 'truthful_accurate_statement');
  const fc = obj(s, 'financial_certification');
  return (
    <NumberedSection
      prefix="Section"
      num={20}
      title="Administrative Documentation"
    >
      <h4 className="text-[14px] font-bold text-navy mt-2 mb-2">
        De Novo Summary (per 21 CFR 860.230)
      </h4>
      <FieldProse
        label="De Novo Summary:"
        body={summary.body}
        fallbackHint="Publicly-disclosable summary of safety + effectiveness data per 21 CFR 860.230"
      />

      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">
        Truthful and Accurate Statement
      </h4>
      <TruthfulAccurateBlock tas={tas} />

      <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">
        Financial Certification or Disclosure (21 CFR Part 54)
      </h4>
      <Gating
        question="Are clinical studies relied upon (triggering 21 CFR Part 54 Financial Certification)?"
        section={fc}
      />
    </NumberedSection>
  );
}

function TruthfulAccurateBlock({ tas }: { tas: Record<string, unknown> }) {
  const today = new Date().toISOString().slice(0, 10);
  const statement = tas.statement;
  const lines: { text: string | unknown; hint: string | null }[] = [
    { text: statement, hint: 'Statement of certification' },
    { text: '', hint: null },
    { text: 'Signature: [electronic signature on file]', hint: null },
    { text: `Name: ${(tas.signatory_name as string) ?? ''}`, hint: 'Signatory name' },
    { text: `Title: ${(tas.signatory_title as string) ?? ''}`, hint: 'Signatory title' },
    { text: `Date: ${(tas.date as string) || today}`, hint: null },
  ];
  return (
    <div className="text-[13.5px] leading-[1.6]">
      {lines.map((line, i) => {
        const text =
          line.text == null
            ? ''
            : typeof line.text === 'string'
              ? line.text
              : String(line.text);
        if (text === '' && line.hint == null) {
          return (
            <p key={i} className="m-0 my-1">
              &nbsp;
            </p>
          );
        }
        if (text === '' && line.hint) {
          return (
            <p key={i} className="m-0 my-1">
              <Placeholder hint={line.hint} />
            </p>
          );
        }
        return (
          <p key={i} className="m-0 my-1">
            {withInlinePlaceholders(text)}
          </p>
        );
      })}
    </div>
  );
}

// ── §21 Amendment / Additional Information Response ────────────────────

function Section21({ dossier }: { dossier: Record<string, unknown> }) {
  return (
    <NumberedSection
      prefix="Section"
      num={21}
      title="Amendment/Additional Information (AI) Response"
    >
      <Gating
        question="Is this submission a response to an FDA Additional Information request?"
        section={obj(dossier, 'section_21_amendment_response')}
      />
    </NumberedSection>
  );
}
